import { useMemo, useCallback, ReactElement, cloneElement } from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Brush,
    ResponsiveContainer,
} from 'recharts';
import { ChartTooltip } from './ChartTooltip';
import {
    PrivacyAwareYAxisTick,
    PrivacyBlurFilter,
    usePrivacyBlurFilterId,
} from '@/components/charts/PrivacyAwareYAxisTick';
import { usePrivacy } from '@/contexts/PrivacyContext';
import { getChartColorsByType } from '@/utils/chartColors';
import { computeChartDomain } from '@/utils/chartDomain';
import type { HistoricalValueItem } from '@/api/types';

interface BrushRange {
    startIndex: number;
    endIndex: number;
}

interface HistoricalValueChartProps {
    /** Historical value data points */
    data: HistoricalValueItem[];
    /** Display currency for tooltip */
    currency: string;
    /** Callback when brush range changes */
    onBrushChange: (range: BrushRange | null) => void;
    /** Whether this is a debt ledger (uses red colors) */
    isDebt: boolean;
    /** Container width for responsive sizing */
    containerWidth: number;
    /** Current brush range (controlled) */
    brushRange?: BrushRange | null;
    /** Custom tooltip component (optional, defaults to ChartTooltip) */
    customTooltip?: ReactElement;
    /** Whether to display Y-axis as percentages (for MWRR mode) */
    isPercentMode?: boolean;
}

/**
 * Calculate responsive chart height based on container width.
 * - Mobile (<640px): 300px
 * - Tablet (640-1024px): 350px
 * - Desktop (>1024px): 400px
 */
function getChartHeight(width: number): number {
    if (width < 640) return 300;
    if (width <= 1024) return 350;
    return 400;
}

/**
 * Format Y-axis values to compact notation (e.g., 10K, 1M).
 * Uses shorter format on mobile to save space.
 */
function formatYAxisValue(value: number, isMobile: boolean = false): string {
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) {
        const formatted = value / 1_000_000;
        // On mobile, use shorter format (no decimal if whole number)
        if (isMobile) {
            return formatted % 1 === 0 ? `${formatted.toFixed(0)}M` : `${formatted.toFixed(1)}M`;
        }
        return `${formatted.toFixed(1)}M`;
    }
    if (absValue >= 1_000) {
        return `${(value / 1_000).toFixed(0)}K`;
    }
    return value.toFixed(0);
}

/**
 * Format Y-axis values as percentages for MWRR mode.
 * Displays values with % suffix (e.g., "10%", "-5%").
 */
function formatYAxisPercent(value: number): string {
    // Handle edge cases
    if (value === 0) return '0%';

    // For small values, show decimal
    if (Math.abs(value) < 1 && value !== 0) {
        return `${value.toFixed(1)}%`;
    }

    return `${Math.round(value)}%`;
}

/**
 * Get Y-axis width based on container width.
 * - Mobile (<640px): 40px (compact)
 * - Desktop: 60px
 */
function getYAxisWidth(containerWidth: number): number {
    return containerWidth < 640 ? 40 : 60;
}

/**
 * Historical value chart component using Recharts.
 * Displays an area chart with gradient fill and a brush for date range selection.
 * Supports both asset (blue) and debt (red) color schemes.
 * Supports custom tooltip components for different data types (Value vs P&L).
 * Supports percentage display mode for MWRR.
 */
export function HistoricalValueChart({
    data,
    currency,
    onBrushChange,
    isDebt,
    containerWidth,
    brushRange,
    customTooltip,
    isPercentMode = false,
}: HistoricalValueChartProps) {
    const { isPrivacyModeEnabled } = usePrivacy();
    const chartHeight = getChartHeight(containerWidth);
    const brushHeight = 40;
    const isMobile = containerWidth < 640;
    const yAxisWidth = getYAxisWidth(containerWidth);
    // Only show brush when there are enough data points for range selection to be meaningful
    const showBrush = data.length >= 4;

    // Transform data for chart (use periodLabel for X axis, value for Y axis)
    // Also calculate value change and growth percentage for tooltip
    const chartData = useMemo(() => {
        return data.map((item, index) => {
            const previousItem = index > 0 ? data[index - 1] : null;
            const previousValue = previousItem?.value ?? null;

            // Calculate value change (current - previous)
            const valueChange =
                item.value !== null && previousValue !== null
                    ? item.value - previousValue
                    : null;

            // Calculate growth percentage ((current - previous) / previous * 100)
            // Handle division by zero
            const growthPercentage =
                valueChange !== null && previousValue !== null && previousValue !== 0
                    ? (valueChange / Math.abs(previousValue)) * 100
                    : null;

            return {
                ...item,
                // Use periodLabel for display, periodEnd for sorting
                name: item.periodLabel,
                // Use 'value' directly (already the aggregated value in display currency)
                chartValue: item.value ?? 0,
                // For tooltip: change data
                previousValue,
                valueChange,
                growthPercentage,
            };
        });
    }, [data]);

    // Compute tight Y-axis domain so small variations are visible
    const yDomain = useMemo(() => {
        return computeChartDomain(chartData.map((d) => d.chartValue));
    }, [chartData]);

    // Handle brush change
    const handleBrushChange = useCallback(
        (brushState: { startIndex?: number; endIndex?: number } | null) => {
            if (!brushState || brushState.startIndex === undefined || brushState.endIndex === undefined) {
                onBrushChange(null);
                return;
            }
            onBrushChange({
                startIndex: brushState.startIndex,
                endIndex: brushState.endIndex,
            });
        },
        [onBrushChange]
    );

    // Color scheme based on ledger type - use global chart colors
    const baseColors = useMemo(() => getChartColorsByType(isDebt ? 'debt' : 'asset'), [isDebt]);
    // Extend with dark mode variants (using same colors for consistency)
    const colors = useMemo(() => ({
        ...baseColors,
        strokeDark: baseColors.stroke,
        fillDark: baseColors.fill,
        gradientStartDark: baseColors.gradientStart,
        gradientEndDark: baseColors.gradientEnd,
    }), [baseColors]);

    // Unique gradient ID for this chart instance
    const gradientId = useMemo(() => `chartGradient-${isDebt ? 'debt' : 'asset'}`, [isDebt]);
    const blurFilterId = usePrivacyBlurFilterId();

    // Create tooltip content - use custom tooltip if provided, otherwise use ChartTooltip
    const tooltipContent = useMemo(() => {
        if (customTooltip) {
            // Clone the custom tooltip to pass through Recharts props
            return cloneElement(customTooltip);
        }
        return <ChartTooltip currency={currency} isDebt={isDebt} />;
    }, [customTooltip, currency, isDebt]);

    // Empty state
    if (data.length === 0) {
        return (
            <div
                className="flex items-center justify-center text-gray-500 dark:text-gray-400"
                style={{ height: chartHeight }}
            >
                <p>No historical data yet. Add monthly values to see trends.</p>
            </div>
        );
    }

    return (
        <div
            className="w-full chart-container"
            style={{ height: chartHeight + (showBrush ? brushHeight + 20 : 0) }}
        >
            <style>{`
                .chart-container .recharts-cartesian-grid-horizontal line,
                .chart-container .recharts-cartesian-grid-vertical line {
                    stroke: #e5e7eb;
                }
                .dark .chart-container .recharts-cartesian-grid-horizontal line,
                .dark .chart-container .recharts-cartesian-grid-vertical line {
                    stroke: #374151;
                }
                .chart-container .recharts-brush-slide {
                    fill: ${colors.fill};
                    fill-opacity: 0.2;
                }
                .dark .chart-container .recharts-brush-slide {
                    fill: ${colors.fillDark};
                    fill-opacity: 0.3;
                }
                .chart-container .recharts-brush-traveller rect {
                    fill: ${colors.stroke};
                }
                .dark .chart-container .recharts-brush-traveller rect {
                    fill: ${colors.strokeDark};
                }
            `}</style>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                    <defs>
                        <PrivacyBlurFilter id={blurFilterId} />
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop
                                offset="5%"
                                className="chart-gradient-start"
                                stopColor={colors.gradientStart.replace(/rgba?\([^)]+\)/, colors.fill)}
                                stopOpacity={0.3}
                            />
                            <stop
                                offset="95%"
                                className="chart-gradient-end"
                                stopColor={colors.gradientEnd.replace(/rgba?\([^)]+\)/, colors.fill)}
                                stopOpacity={0.05}
                            />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        dy={10}
                        className="text-gray-600 dark:text-gray-400"
                    />
                    <YAxis
                        key={`${String(isPrivacyModeEnabled)}-${isPercentMode}`}
                        domain={yDomain}
                        tick={
                            <PrivacyAwareYAxisTick
                                formatValue={(value) =>
                                    isPercentMode
                                        ? formatYAxisPercent(value)
                                        : formatYAxisValue(value, isMobile)
                                }
                                /* Percentages are not money, so they stay readable. */
                                blurred={isPrivacyModeEnabled && !isPercentMode}
                                blurFilterId={blurFilterId}
                                fontSize={isMobile ? 10 : 12}
                            />
                        }
                        tickLine={false}
                        axisLine={false}
                        width={yAxisWidth}
                    />
                    <Tooltip
                        content={tooltipContent}
                        cursor={{ stroke: colors.stroke, strokeWidth: 1, strokeDasharray: '3 3' }}
                    />
                    <Area
                        type="monotone"
                        dataKey="chartValue"
                        stroke={colors.stroke}
                        strokeWidth={2}
                        fill={`url(#${gradientId})`}
                        animationDuration={300}
                        animationEasing="ease-out"
                    />
                    {showBrush && (
                        <Brush
                            dataKey="name"
                            height={brushHeight}
                            stroke={colors.stroke}
                            fill="transparent"
                            onChange={handleBrushChange}
                            startIndex={brushRange?.startIndex}
                            endIndex={brushRange?.endIndex}
                            travellerWidth={10}
                        >
                            <AreaChart data={chartData}>
                                <Area
                                    type="monotone"
                                    dataKey="chartValue"
                                    stroke={colors.stroke}
                                    fill={colors.fill}
                                    fillOpacity={0.2}
                                />
                            </AreaChart>
                        </Brush>
                    )}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}
